import LogRocket from 'logrocket'
import setupLogRocketReact from 'logrocket-react'
import * as Sentry from '@sentry/react'
import type { Middleware } from 'redux'
import type { LoggerProvider } from './types'
import type { LoggerUser } from '../types'
import { sanitizeObject } from '../sensitiveFields'
import { isSandbox } from 'src/utils/env'

function toLogRocketExtra(
  data: Record<string, unknown> | undefined,
): Record<string, string> | undefined {
  if (!data) {
    return undefined
  }

  const result: Record<string, string> = {}
  for (const [key, value] of Object.entries(data)) {
    result[key] = typeof value === 'string' ? value : JSON.stringify(value)
  }

  return result
}

const EXCLUDED_NETWORK_PATTERNS = [
  '/auth/refresh',
  '/dashboards/updates/',
  '/updates/failed_payments',
  'intercom.io',
  'sentry.io',
]

const LOGROCKET_BLOCKED_PARTNER_IDS: string[] = [
  '9737e167-2032-4d65-8ede-52a039860cb4',
  ...(process.env.REACT_APP_LOGROCKET_BLOCKED_PARTNER_IDS?.split(',')
    .map((id) => id.trim())
    .filter(Boolean) ?? []),
]

const DEFAULT_SEGMENT_RATES: Record<string, number> = {
  dashboard: 0.8,
  auth: 0.8,
  onboarding: 0.2,
  other: 0.2,
}

function getSegmentRates(): Record<string, number> {
  const parse = (v: string | undefined, fallback: number): number => {
    if (v == null || v === '') {
      return fallback
    }

    const n = Number(v)

    return Number.isFinite(n) && n >= 0 && n <= 1 ? n : fallback
  }

  return {
    dashboard: parse(
      process.env.REACT_APP_LOGROCKET_RATE_DASHBOARD,
      DEFAULT_SEGMENT_RATES.dashboard,
    ),
    auth: parse(process.env.REACT_APP_LOGROCKET_RATE_AUTH, DEFAULT_SEGMENT_RATES.auth),
    onboarding: parse(
      process.env.REACT_APP_LOGROCKET_RATE_ONBOARDING,
      DEFAULT_SEGMENT_RATES.onboarding,
    ),
    other: parse(process.env.REACT_APP_LOGROCKET_RATE_OTHER, DEFAULT_SEGMENT_RATES.other),
  }
}

let sessionHadError = false

export class LogRocketProvider implements LoggerProvider {
  private initialized = false
  private sessionURL: string | null = null
  private sessionURLCallbacks: Array<(url: string) => void> = []

  init(): void {
    const appId = process.env.REACT_APP_LOGROCKET_APP_ID

    if (process.env.REACT_APP_LOGROCKET_RECORDING_DISABLED === 'true' || isSandbox()) {
      return
    }

    if (this.initialized || !appId) {
      return
    }

    const segment: string =
      (typeof window !== 'undefined' &&
        (window as Window & { __lrSegment?: string }).__lrSegment) ||
      'other'
    const rates = getSegmentRates()
    const rate = rates[segment] ?? rates.other
    const sessionSampled = Math.random() < rate

    LogRocket.init(appId, {
      release: process.env.REACT_APP_VERSION,
      mergeIframes: true,
      shouldSendData(): boolean {
        if (
          typeof window !== 'undefined' &&
          (window as Window & { __lrBlockSession?: boolean }).__lrBlockSession
        ) {
          return false
        }

        if (sessionHadError) {
          return true
        }

        if (typeof window !== 'undefined') {
          const params = new URLSearchParams(window.location.search)

          if (params.get('lr_capture') === '1') {
            return true
          }

          try {
            if (window.localStorage.getItem('logrocket_capture') === 'true') {
              return true
            }
          } catch {}
        }

        return sessionSampled
      },
      console: {
        shouldAggregateConsoleErrors: true,
        isEnabled: {
          log: true,
          debug: false,
          info: true,
          warn: false,
          error: true,
        },
      },
      network: {
        requestSanitizer: (request) => {
          if (EXCLUDED_NETWORK_PATTERNS.some((pattern) => request.url.includes(pattern))) {
            return null
          }

          if (request.headers?.Authorization) {
            request.headers.Authorization = '[REDACTED]'
          }

          return request
        },
        responseSanitizer: (response) => {
          if (response.body) {
            try {
              const body = JSON.parse(response.body)

              if (body.accessToken) {
                body.accessToken = '[REDACTED]'
              }

              if (body.refreshToken) {
                body.refreshToken = '[REDACTED]'
              }

              if (body.token) {
                body.token = '[REDACTED]'
              }

              response.body = JSON.stringify(body)
            } catch {}
          }

          return response
        },
      },
      dom: { inputSanitizer: true },
    })

    setupLogRocketReact()
    LogRocket.getSessionURL((url) => {
      this.sessionURL = url
      this.sessionURLCallbacks.forEach((cb) => cb(url))
      this.sessionURLCallbacks = []
    })

    this.initialized = true
  }

  identify(user: LoggerUser): void {
    if (!this.initialized) {
      return
    }

    const partnerId = user.partner_id

    if (typeof partnerId === 'string' && LOGROCKET_BLOCKED_PARTNER_IDS.includes(partnerId)) {
      if (typeof window !== 'undefined') {
        void ((window as Window & { __lrBlockSession?: boolean }).__lrBlockSession = true)
      }

      return
    }

    const { id, ...traits } = user
    const cleanTraits: Record<string, string | number | boolean> = {}

    for (const [key, value] of Object.entries(traits)) {
      if (value !== undefined) {
        cleanTraits[key] = value
      }
    }

    LogRocket.identify(id, cleanTraits)
  }

  error(message: string, error?: Error, data?: Record<string, unknown>): void {
    if (!this.initialized) {
      return
    }

    sessionHadError = true

    const errorObj = error || new Error(message)

    LogRocket.captureException(errorObj, {
      extra: toLogRocketExtra(data),
    })

    Sentry.captureException(errorObj, (scope) => {
      scope.addBreadcrumb({ category: 'error', message, level: 'error' })

      if (data) {
        scope.setContext('data', data)
      }

      if (this.sessionURL) {
        scope.setTag('logrocket_session', this.sessionURL)
      }

      return scope
    })
  }

  warn(message: string, data?: Record<string, unknown>): void {
    if (!this.initialized) {
      return
    }

    LogRocket.log(message, data)
    Sentry.addBreadcrumb({ category: 'warning', message, level: 'warning', data })
  }

  info(message: string, data?: Record<string, unknown>): void {
    if (!this.initialized) {
      return
    }

    LogRocket.log(message, data)
    Sentry.addBreadcrumb({ category: 'info', message, level: 'info', data })
  }

  track(eventName: string, properties?: Record<string, string | number | boolean>): void {
    if (!this.initialized) {
      return
    }

    LogRocket.track(eventName, properties)
  }

  getSessionURL(): string | null {
    return this.sessionURL
  }

  onSessionURLReady(callback: (url: string) => void): void {
    if (this.sessionURL) {
      callback(this.sessionURL)
    } else {
      this.sessionURLCallbacks.push(callback)
    }
  }

  createReduxMiddleware(): Middleware {
    if (!process.env.REACT_APP_LOGROCKET_APP_ID) {
      return () => (next) => (action) => next(action)
    }

    return LogRocket.reduxMiddleware({
      stateSanitizer: (state) => {
        if (!state) {
          return state
        }

        return sanitizeObject(state)
      },
      actionSanitizer: (action) => {
        if (!action) {
          return action
        }

        return sanitizeObject(action)
      },
    })
  }
}
